// Copyright 2022 Carnegie Mellon University. All Rights Reserved.
// Released under a MIT (SEI)-style license, please see LICENSE.md in the
// project root for license information or contact permission@sei.cmu.edu for full terms.

import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Input,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { Sort, MatSortable } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TeamQuery } from 'src/app/data/team/team.query';
import { SystemPermission, Team, TeamUser, User, UserTeamRole, MselUnit, Unit, CiteService } from 'src/app/generated/blueprint.api';
import { TeamUserDataService } from 'src/app/data/team-user/team-user-data.service';
import { TeamUserQuery } from 'src/app/data/team-user/team-user.query';
import { CurrentUserQuery, UserQuery } from 'src/app/data/user/user.query';
import { UserTeamRoleDataService } from 'src/app/data/user-team-role/user-team-role-data.service';
import { UserTeamRoleQuery } from 'src/app/data/user-team-role/user-team-role.query';
import { MselPlus } from 'src/app/data/msel/msel-data.service';
import { MselQuery } from 'src/app/data/msel/msel.query';
import { MselUnitQuery } from 'src/app/data/msel-unit/msel-unit.query';
import { UnitQuery } from 'src/app/data/unit/unit.query';
import { PermissionDataService } from 'src/app/data/permission/permission-data.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
@Component({
  selector: 'app-team-users',
  templateUrl: './team-users.component.html',
  styleUrls: ['./team-users.component.scss'],
  standalone: false
})
export class TeamUsersComponent implements OnDestroy, OnInit, AfterViewInit {
  @Input() team: Team;
  @ViewChild('userPaginator') userPaginator: MatPaginator;
  @ViewChild('teamMemberPaginator') teamMemberPaginator: MatPaginator;
  userList: User[] = [];
  teamUsers: TeamUser[] = [];
  otherTeamUsers: TeamUser[] = [];
  teamList: Team[] = [];
  userTeamRoles: UserTeamRole[] = [];
  mselUnits: MselUnit[] = [];
  units: Unit[] = [];
  minUserColumns: string[] = ['name', 'id'];
  displayedTeamUserColumns: string[] = ['name', 'type', 'id'];
  userDataSource = new MatTableDataSource<User>(new Array<User>());
  teamUserDataSource = new MatTableDataSource<TeamUser>(new Array<TeamUser>());
  filterControl = new UntypedFormControl();
  teamMemberFilterControl = new UntypedFormControl();
  filterString = '';
  teamMemberFilterString = '';
  sort: Sort = { active: 'name', direction: 'asc' };
  teamRoles: string[] = [];
  msel = new MselPlus();
  loggedInUserId = '';
  private unsubscribe$ = new Subject();

  constructor(
    private teamQuery: TeamQuery,
    private teamUserDataService: TeamUserDataService,
    private teamUserQuery: TeamUserQuery,
    private userQuery: UserQuery,
    private currentUserQuery: CurrentUserQuery,
    private userTeamRoleDataService: UserTeamRoleDataService,
    private userTeamRoleQuery: UserTeamRoleQuery,
    private mselQuery: MselQuery,
    private mselUnitQuery: MselUnitQuery,
    private unitQuery: UnitQuery,
    private permissionDataService: PermissionDataService,
    private citeService: CiteService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    // Fetch CITE team roles dynamically
    this.citeService.getTeamRoles().pipe(takeUntil(this.unsubscribe$)).subscribe(roles => {
      this.teamRoles = roles.map(r => r.name).filter(n => !!n);
    });
    this.userQuery.selectAll().pipe(takeUntil(this.unsubscribe$)).subscribe(users => {
      this.userList = users;
      this.setDataSources();
    });
    this.teamUserQuery.selectAll().pipe(takeUntil(this.unsubscribe$)).subscribe(tUsers => {
      this.teamUsers = tUsers.filter(tu => tu.teamId === this.team?.id);
      this.otherTeamUsers = tUsers.filter(tu => tu.teamId !== this.team?.id);
      this.setDataSources();
    });
    // observe the evaluation teams
    this.teamQuery.selectAll().pipe(takeUntil(this.unsubscribe$)).subscribe(teams => {
      this.teamList = teams ? teams : [];
    });
    // subscribe to UserTeamRoles
    this.userTeamRoleQuery.selectAll().pipe(takeUntil(this.unsubscribe$)).subscribe(umrs => {
      this.userTeamRoles = umrs;
    });
    // subscribe to MselUnits to know which users are contributors
    this.mselUnitQuery.selectAll().pipe(takeUntil(this.unsubscribe$)).subscribe(mselUnits => {
      this.mselUnits = mselUnits || [];
      this.applyFilter();
    });
    // subscribe to Units to get user lists
    this.unitQuery.selectAll().pipe(takeUntil(this.unsubscribe$)).subscribe(units => {
      this.units = units || [];
      this.applyFilter();
    });
  }

  ngOnInit() {
    // Load permissions and trigger change detection when loaded
    this.permissionDataService.load()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });
    // Subscribe to the active MSEL
    (this.mselQuery.selectActive() as Observable<MselPlus>)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(msel => {
        if (msel) {
          Object.assign(this.msel, msel);
        }
      });
    // Subscribe to current user
    this.currentUserQuery.select()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(cu => {
        if (cu.id) {
          this.loggedInUserId = cu.id;
        }
      });
    this.filterControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.filterString = this.filterControl.value;
      this.applyFilter();
    });
    this.userQuery.selectAll().pipe(takeUntil(this.unsubscribe$)).subscribe(users => {
      this.userList = users;
      this.applyFilter();
    });
    this.filterControl.setValue('');
    this.teamMemberFilterControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.teamMemberFilterString = this.teamMemberFilterControl.value;
      this.applyTeamMemberFilter();
    });
    this.teamUsers = this.teamUserQuery.getAll().filter(tu => tu.teamId === this.team?.id);
    this.otherTeamUsers = this.teamUserQuery.getAll().filter(tu => tu.teamId !== this.team?.id);
    this.setDataSources();
  }

  ngAfterViewInit() {
    this.userDataSource.paginator = this.userPaginator;
    this.teamUserDataSource.paginator = this.teamMemberPaginator;
  }

  canEditMsel(): boolean {
    return this.permissionDataService.hasPermission(SystemPermission.ManageMsels) ||
      this.msel.hasRole(this.loggedInUserId, '').owner;
  }

  clearFilter() {
    this.filterControl.setValue('');
  }

  clearTeamMemberFilter() {
    this.teamMemberFilterControl.setValue('');
  }

  applyTeamMemberFilter() {
    const searchTerm = this.teamMemberFilterControl.value ? this.teamMemberFilterControl.value.toLowerCase() : '';
    const filteredTeamUsers = this.teamUsers.filter(tu =>
      !searchTerm || this.getUserName(tu.userId).toLowerCase().includes(searchTerm)
    );
    this.sortTeamUserData(filteredTeamUsers);
  }

  setDataSources() {
    // Now that all of the observables are returned, process accordingly.
    // get users from the TeamUsers
    // sort the list and add it as the data source
    this.teamUserDataSource.data = this.teamUsers.sort((a, b) => {
      const aName = this.getUserName(a.userId).toLowerCase();
      const bName = this.getUserName(b.userId).toLowerCase();
      if (aName < bName) {
        return -1;
      } else if (aName > bName) {
        return 1;
      } else {
        return 0;
      }
    });
    const newAllUsers = !this.userList ? new Array<User>() : this.userList.slice(0);
    this.teamUserDataSource.data.forEach((tu) => {
      const index = newAllUsers.findIndex((u) => u.id === tu.userId);
      if (index >= 0) {
        newAllUsers.splice(index, 1);
      }
    });
    this.userDataSource = new MatTableDataSource(newAllUsers);
    if (this.userPaginator) {
      this.userDataSource.paginator = this.userPaginator;
    }
    if (this.teamMemberPaginator) {
      this.teamUserDataSource.paginator = this.teamMemberPaginator;
    }
  }

  applyFilter() {
    const searchTerm = this.filterControl.value ? this.filterControl.value.toLowerCase() : '';

    // Get users who are part of contributor units for this MSEL
    const contributorUserIds = new Set<string>();
    this.mselUnits.forEach(mselUnit => {
      // Try to get users from the mselUnit.unit relationship first
      if (mselUnit.unit && mselUnit.unit.users) {
        mselUnit.unit.users.forEach(user => {
          contributorUserIds.add(user.id);
        });
      } else if (mselUnit.unitId) {
        // If unit relationship not populated, look up the unit directly
        const unit = this.units.find(u => u.id === mselUnit.unitId);
        if (unit && unit.users) {
          unit.users.forEach(user => {
            contributorUserIds.add(user.id);
          });
        }
      }
    });

    // Get users not already on this team
    const teamUserIds = new Set(this.teamUsers.map(tu => tu.userId));

    // Filter to only contributors who are not already on the team
    const availableUsers = this.userList.filter(user =>
      contributorUserIds.has(user.id) && !teamUserIds.has(user.id)
    );

    // Apply search filter
    const filteredData = availableUsers.filter(user =>
      !searchTerm || user.name.toLowerCase().includes(searchTerm)
    );
    this.sortUserData(filteredData);
  }

  sortUserData(data: User[]) {
    data.sort((a, b) => {
      const isAsc = this.sort.direction === 'asc';
      switch (this.sort.active) {
        case 'name':
          return this.compare(a.name, b.name, isAsc);
        case 'id':
          return this.compare(a.id, b.id, isAsc);
        default:
          return 0;
      }
    });
    this.userDataSource.data = data;
    if (this.userPaginator) {
      this.userDataSource.paginator = this.userPaginator;
    }
  }

  sortTeamUserData(teamUserData: TeamUser[]) {
    teamUserData.sort((a, b) => {
      const aName = this.getUserName(a.userId).toLowerCase();
      const bName = this.getUserName(b.userId).toLowerCase();
      const isAsc = this.sort.direction === 'asc';
      switch (this.sort.active) {
        case 'name':
          return this.compare(aName, bName, isAsc);
        default:
          return 0;
      }
    });
    this.teamUserDataSource.data = teamUserData;
    if (this.teamMemberPaginator) {
      this.teamUserDataSource.paginator = this.teamMemberPaginator;
    }
  }

  compare(a: string, b: string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  onSortChange(sort: Sort) {
    this.sort = sort;
    this.applyFilter();
  }

  onSortTeamChange(sort: Sort) {
    this.sort = sort;
    this.sortTeamUserData(this.teamUsers);
  }

  getUserName(id: string) {
    const user = this.userList.find(u => u.id === id);
    return user ? user.name : '?';
  }

  addUserToTeam(user: User): void {
    const teamUser = {
      teamId: this.team?.id,
      userId: user.id
    } as TeamUser;
    this.teamUserDataService.add(teamUser);
  }

  /**
   * Removes a user from the current team
   * @param user The user to remove from team
   */
  removeUserFromTeam(teamUser: TeamUser): void {
    this.teamUserDataService.delete(teamUser.id);
  }

  getUserTeamRole(userId: string): string {
    const role = this.userTeamRoles.find(utr =>
      utr.userId === userId && utr.teamId === this.team?.id
    );
    return role ? role.role : '';
  }

  setUserTeamRole(userId: string, teamId: string, newRole: string) {
    // Remove existing role for this user on this team
    const existingRole = this.userTeamRoles.find(utr =>
      utr.userId === userId && utr.teamId === teamId
    );
    if (existingRole) {
      this.userTeamRoleDataService.delete(existingRole.id);
    }
    // Add the new role if one was selected
    if (newRole) {
      const umr = {
        userId: userId,
        teamId: teamId,
        role: newRole
      } as UserTeamRole;
      this.userTeamRoleDataService.add(umr);
    }
  }

  onAnotherTeam(userId: string): boolean {
    return this.otherTeamUsers.some(tu => tu.userId === userId);
  }

  getUserTeamName(userId: string): string {
    let teamName = '';
    const teamUser = this.otherTeamUsers.find(tu => tu.userId === userId);
    if (teamUser && teamUser.teamId) {
      const team = this.teamList.find(t => t.id === teamUser.teamId);
      if (team) {
        teamName = team.shortName;
      }
    }
    return teamName;
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

}
